function getFocusableElements(container) {
  return Array.from(container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"));
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute("form", `${element.form.getAttribute("id")}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (elementToFocus.tagName === "INPUT" && ["search", "text", "email", "url"].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ["ARROWUP", "ARROWDOWN", "ARROWLEFT", "ARROWRIGHT", "TAB", "ENTER", "SPACE", "ESCAPE", "HOME", "END", "PAGEUP", "PAGEDOWN"];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)));
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    const inventoryQuantity = parseInt(this.input.dataset.quantityVariantInventory);

    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }

    if (inventoryQuantity) {
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value == inventoryQuantity);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)").forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary")) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);

        // Publish event when main menu is opening
        publish("menu-opening", {
          source: "menu-drawer",
          detailsElement,
        });
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
      document.body.classList.add("has-menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    // Publish event when submenu is opening
    publish("menu-opening", {
      source: "menu-drawer",
      detailsElement: this.mainDetailsToggle,
    });
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      if (!details.classList.contains("menu-drawer__item--expanded")) {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
        document.body.classList.remove("has-menu-opening");
      }
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset = this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header && document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace("shopify-section-", "");
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const sourceQty = html.querySelector(".quick-order-list-container").parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`));
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == "VIDEO" && deferredElement.getAttribute("autoplay")) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    // Select slider elements
    this.slider = this.querySelector('[id^="Slider-"]'); // Main slider container
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]'); // All slides
    this.enableSliderLooping = false; // Disable looping by default
    this.currentPageElement = this.querySelector(".slider-counter--current"); // Current page indicator
    this.pageTotalElement = this.querySelector(".slider-counter--total"); // Total page indicator
    this.prevButton = this.querySelector('button[name="previous"]'); // Previous button
    this.nextButton = this.querySelector('button[name="next"]'); // Next button

    if (!this.slider || !this.nextButton) return;

    // Select buttons wrapper
    this.buttonsWrapper = this.querySelector(".slider-buttons"); // Container for navigation buttons

    // Detect orientation (horizontal or vertical)
    this.isVertical = this.dataset.orientation === "vertical"; // Check slider orientation

    // Select dots elements
    this.dotsWrapper = this.querySelector(".slider-counter--dots");
    this.dots = [];
    if (this.dotsWrapper) {
      // Initialize dots and add event listeners for navigation
      this.dots = Array.from(this.dotsWrapper.querySelectorAll(".slider-counter__link"));
      this.dots.forEach((dot, index) => dot.addEventListener("click", () => this.scrollToSlide(index)));
    }

    // **Hide the entire slider component if there are no slides**
    if (!this.sliderItems.length) {
      this.style.display = "none"; // Hide the slider component
      return; // Exit constructor
    }

    // Initialize slider pages and observe size changes
    this.initPages();
    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    // Event listeners for scroll and navigation buttons
    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));

    // Listen for grouping updates
    document.addEventListener("mediaGroupingUpdated", () => {
      this.refresh();
    });
  }

  /**
   * Initialize slider pages and calculate layout
   */
  initPages() {
    // Filter visible slides (exclude hidden slides)
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0 && !element.classList.contains("hidden"));

    // Select the thumbnail slider inside the media-gallery component
    const thumbnailSlider = this.closest("media-gallery")?.querySelector(".thumbnail-slider");

    // **Hide slider-buttons and thumbnail-slider if there is only 1 or no slides**
    if (this.sliderItemsToShow.length < 2) {
      if (this.buttonsWrapper) this.buttonsWrapper.classList.add("hidden"); // Hide navigation buttons
      if (thumbnailSlider) thumbnailSlider.classList.add("hidden"); // Hide thumbnail slider
    } else {
      if (this.buttonsWrapper) this.buttonsWrapper.classList.remove("hidden"); // Show navigation buttons
      if (thumbnailSlider) thumbnailSlider.classList.remove("hidden"); // Show thumbnail slider
    }

    // **Exit early if there are fewer than 2 slides**
    if (this.sliderItemsToShow.length < 2) {
      this.totalPages = 1;
      if (this.pageTotalElement) this.pageTotalElement.textContent = this.totalPages; // Update total page count
      return; // Stop further processing
    }

    // **Fix 'offset' calculation for vertical or horizontal orientation**
    if (this.sliderItemsToShow.length > 1) {
      this.sliderItemOffset = this.isVertical
        ? this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop // Vertical
        : this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft; // Horizontal
    } else {
      this.sliderItemOffset = this.isVertical
        ? this.sliderItemsToShow[0].clientHeight // Vertical
        : this.sliderItemsToShow[0].clientWidth; // Horizontal
    }

    // Calculate total pages
    const sliderSize = this.isVertical ? this.slider.clientHeight : this.slider.clientWidth;
    const offsetStart = this.isVertical ? this.sliderItemsToShow[0].offsetTop : this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((sliderSize - offsetStart) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

    // Update slider state
    this.update();
  }

  /**
   * Refresh slider layout and controls
   */
  refresh() {
    this.resetPages(); // Reset visible pages
    this.update(); // Update controls and navigation
  }

  /**
   * Reset pages and reinitialize layout
   */
  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]'); // Re-select all slides
    this.initPages(); // Recalculate layout
  }

  /**
   * Update slider navigation, controls, and dots
   */
  update() {
    if (!this.slider || !this.nextButton) return;

    // Hide buttons if fewer than 2 slides
    if (this.sliderItemsToShow.length < 2 && this.buttonsWrapper) {
      this.buttonsWrapper.classList.add("hidden");
      return;
    } else if (this.buttonsWrapper) {
      this.buttonsWrapper.classList.remove("hidden");
    }

    // Calculate the current page
    const previousPage = this.currentPage;
    const scrollOffset = this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft;
    this.currentPage = Math.round(scrollOffset / this.sliderItemOffset) + 1;

    // Update page counters
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    // Dispatch slideChanged event
    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    // Update dot states
    if (this.dots.length && this.currentPage <= this.dots.length) {
      this.dots.forEach((dot) => {
        dot.classList.remove("slider-counter__link--active");
        dot.removeAttribute("aria-current");
      });
      const activeDot = this.dots[this.currentPage - 1];
      if (activeDot) {
        activeDot.classList.add("slider-counter__link--active");
        activeDot.setAttribute("aria-current", "true");
      }
    }

    // Update navigation button states
    if (this.enableSliderLooping) return;

    const scrollStart = this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft;
    const scrollEnd = this.isVertical ? this.slider.scrollHeight : this.slider.scrollWidth;

    if (scrollStart === 0) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (scrollStart + (this.isVertical ? this.slider.clientHeight : this.slider.clientWidth) >= scrollEnd) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  /**
   * Navigate slides using previous or next buttons
   */
  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    const scrollPosition = event.currentTarget.name === "next" ? (this.isVertical ? this.slider.scrollTop + step * this.sliderItemOffset : this.slider.scrollLeft + step * this.sliderItemOffset) : this.isVertical ? this.slider.scrollTop - step * this.sliderItemOffset : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(scrollPosition);
  }

  /**
   * Scroll smoothly to a given position
   */
  setSlidePosition(position) {
    this.slider.scrollTo({
      [this.isVertical ? "top" : "left"]: position,
      behavior: "smooth",
    });
  }

  /**
   * Scroll to a slide based on dot index
   */
  scrollToSlide(index) {
    const position = this.sliderItemOffset * index;
    this.setSlidePosition(position);
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active");
    this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === "next" && !isLastSlide) || (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.selectedOptions = [];
    this.productVariants = this.querySelector("[data-variants]")?.innerHTML;
    this.productJson = JSON.parse(this.productVariants);
  }

  connectedCallback() {
    this.rebuildOptions(this);
    this.attachChangeListeners(this);

    const labelElements = this.querySelectorAll("label");

    labelElements.forEach((labelElement) => {
      if (labelElement.hasAttribute("data-sizes")) {
        this.checkLabel(labelElement);
      }
    });

    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.rebuildOptions(this);
      this.attachChangeListeners(this);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  // Check if a variant is In Stock
  // inStock(inputValue, optionLevel) {
  //   for (let i = 0; i < this.productJson.length; i++) {
  //     if (optionLevel === 1) {
  //       if (this.productJson[i].option1 === this.selectedOptions[0] && this.productJson[i].option2 === inputValue && this.productJson[i].inventory_quantity > 0) {
  //         return true;
  //       }
  //     } else if (optionLevel === 2) {
  //       if (this.productJson[i].option1 === this.selectedOptions[0] && this.productJson[i].option2 === this.selectedOptions[1] && this.productJson[i].option3 === inputValue && this.productJson[i].inventory_quantity > 0) {
  //         return true;
  //       }
  //     }
  //   }
  //   return false;
  // }
  inStock(inputValue, optionLevel) {
    return this.productJson.some((variant) => {
      const options = variant.options;

      if (optionLevel === 1) {
        return options[1] === this.selectedOptions[0] && options[0] === inputValue && variant.inventory_quantity > 0;
      } else if (optionLevel === 2) {
        return options[1] === this.selectedOptions[0] && options[0] === this.selectedOptions[1] && options[2] === inputValue && variant.inventory_quantity > 0;
      }
      return false;
    });
  }

  // Check if a combination of options is valid
  // checkAvailable(inputValue, optionLevel) {
  //   for (let i = 0; i < this.productJson.length; i++) {
  //     if (optionLevel === 1) {
  //       if (this.productJson[i].option1 === this.selectedOptions[0] && this.productJson[i].option2 === inputValue) {
  //         return true;
  //       }
  //     } else if (optionLevel === 2) {
  //       if (this.productJson[i].option1 === this.selectedOptions[0] && this.productJson[i].option2 === this.selectedOptions[1] && this.productJson[i].option3 === inputValue) {
  //         return true;
  //       }
  //     }
  //   }
  //   return false;
  // }
  checkAvailable(inputValue, optionLevel) {
    return this.productJson.some((variant) => {
      const options = variant.options;

      if (optionLevel === 1) {
        return options[1] === this.selectedOptions[0] && options[0] === inputValue;
      } else if (optionLevel === 2) {
        return options[1] === this.selectedOptions[0] && options[0] === this.selectedOptions[1] && options[2] === inputValue;
      }
      return false;
    });
  }

  // Rebuild options to enable/disable and show/hide based on valid combinations
  rebuildOptions(variantSelects) {
    const pickerType = variantSelects.querySelectorAll("fieldset").length > 0 ? "buttons" : "selects";
    const fieldsets = pickerType === "buttons" ? Array.from(variantSelects.querySelectorAll("fieldset")).toReversed() : Array.from(variantSelects.querySelectorAll(".product-form__input--dropdown")).toReversed();

    if (!fieldsets.length) {
      console.warn("No fieldsets or dropdowns found in variantSelects.");
      return;
    }

    this.selectedOptions = fieldsets.map((fieldset, index) => {
      if (pickerType === "buttons") {
        const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
        return checkedInput ? checkedInput.value : null;
      } else {
        const select = fieldset.querySelector("select");
        return select ? select.value : null;
      }
    });

    // console.log("Selected Options:", this.selectedOptions);

    for (let optionLevel = 1; optionLevel < fieldsets.length; optionLevel++) {
      const inputs = pickerType === "buttons" ? fieldsets[optionLevel].querySelectorAll('input[type="radio"]') : fieldsets[optionLevel].querySelectorAll("option");

      if (!inputs.length) {
        continue;
      }

      inputs.forEach((input) => {
        const isAvailable = this.checkAvailable(input.value, optionLevel);
        const isInStock = this.inStock(input.value, optionLevel);

        // Disabling Unavailable inputs
        input.disabled = !isAvailable | !isInStock;

        // Toggling classes
        input.classList.toggle("hidden", !isAvailable);
        input.classList.toggle("disabled", !isInStock);

        // Handle "buttons" picker type
        if (pickerType === "buttons") {
          const label = fieldsets[optionLevel].querySelector(`label[for="${input.id}"]`);
          label?.classList.toggle("hidden", !isAvailable);
        }
      });

      if (pickerType === "buttons") {
        const checkedInput = fieldsets[optionLevel].querySelector('input[type="radio"]:checked');
        if (checkedInput?.disabled) {
          const firstValidInput = Array.from(inputs).find((input) => !input.disabled);
          if (firstValidInput) {
            firstValidInput.checked = true;
          }
        }
      } else {
        const select = fieldsets[optionLevel].querySelector("select");
        const selectedOption = select?.querySelector("option:checked");
        if (selectedOption?.disabled) {
          const firstValidOption = Array.from(inputs).find((option) => !option.disabled);
          if (firstValidOption) {
            select.value = firstValidOption.value;
          }
        }
      }
    }
  }

  // Attach change listeners to all dropdowns and radio buttons
  attachChangeListeners(variantSelects) {
    const pickerType = variantSelects.querySelectorAll("fieldset").length > 0 ? "buttons" : "selects";
    const fieldsets = pickerType === "buttons" ? Array.from(variantSelects.querySelectorAll("fieldset")).toReversed() : Array.from(variantSelects.querySelectorAll(".product-form__input--dropdown")).toReversed();

    if (!fieldsets.length) {
      return;
    }

    fieldsets.forEach((fieldset, index) => {
      if (pickerType === "buttons") {
        const buttons = fieldset.querySelectorAll('input[type="radio"]');
        buttons.forEach((radio) => {
          radio.addEventListener("change", () => {
            // console.log(`Option ${index + 1} changed to: ${radio.value}`);
            this.rebuildOptions(variantSelects);
          });
        });
      } else {
        const select = fieldset.querySelector("select");
        if (select) {
          select.addEventListener("change", () => {
            // console.log(`Option ${index + 1} changed to: ${select.value}`);
            this.rebuildOptions(variantSelects);
          });
        }
      }
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.this.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.this.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.this.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target.closest(".product-form__input").querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty("--swatch-focal-point", target.this.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset");
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.this.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(({ dataset }) => dataset.optionValueId);
  }

  /**
   * Update size availability based on the provided data and manage sold-out state.
   */
  checkLabel(labelElement) {
    if (!labelElement) return; // Exit if no size data or container is available
    const sizesData = labelElement.getAttribute("data-sizes");

    // Parse sizes data into a map of size -> availability
    const sizeMap = sizesData.split(",").reduce((map, sizePair) => {
      const [size, available] = sizePair.split(":").map((v) => v.trim());
      map[size] = available === "true"; // Convert availability to boolean
      return map;
    }, {});

    // Check if all sizes are sold out
    const allSoldOut = Object.values(sizeMap).every((available) => !available);

    if (allSoldOut) {
      labelElement.classList.add("disabled");
    } else {
      labelElement.classList.remove("disabled");
    }
  }
}

customElements.define("variant-selects", VariantSelects);

class ExVariantSelects extends HTMLElement {
  constructor() {
    super();
    this.inputs = this.querySelectorAll('input[type="radio"]');
    this.targetInput = document.querySelector('.product-variant-id');
    this.priceItem = document.querySelector('.price-item--regular');
  }

  connectedCallback() {
    this.inputs.forEach((input) => {
      input.addEventListener('change', this.updateVariantId.bind(this));
    });
  }

  disconnectedCallback() {
    this.inputs.forEach((input) => {
      input.removeEventListener('change', this.updateVariantId);
    });
  }

  updateVariantId(event) {
    const selectedInput = event.target;
    if (this.targetInput && selectedInput.checked) {
      this.targetInput.value = selectedInput.id;

      const price = selectedInput.getAttribute('data-price');
      if (this.priceItem && price) {
        this.priceItem.textContent = price;
      }
    }
  }
}

customElements.define('ex-variant-selects', ExVariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener("storefront:signincompleted", this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace("[min]", event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace("[max]", event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace("[step]", event.target.step));
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class CardOptions extends HTMLElement {
  constructor() {
    super();
    // Select elements related to color and size options
    this.colorOptions = this.querySelectorAll(".variant-color");
    this.sizeContainer = this.closest(".card").querySelector(".option-values");
    this.sizeOptions = this.sizeContainer.querySelectorAll(".size-option");
    this.thumbnail = this.closest(".card").querySelector(".card__media img");
    this.defaultImage = this.getAttribute("data-default-image");
    this.currentActiveColor = null; // Track the currently active color
    this.overviewSelected = this.querySelector(".product-option__overview .selected");

    // Initialize status for each variant color
    this.colorOptions.forEach((colorOption) => {
      this.updateSizes(colorOption.getAttribute("data-sizes"), colorOption); // Update sizes on load
    });

    // Sort and filter size options based on 'order' and limit display to the first 5 items
    this.sortAndFilterSizeOptions();

    // Add event listeners for hover to re-check sizes
    this.colorOptions.forEach((colorOption) => {
      colorOption.addEventListener("mouseenter", () => this.setActiveColor(colorOption));
    });

    // Set the first available color as the default active color
    const firstAvailableColor = Array.from(this.colorOptions).find((colorOption) => !colorOption.classList.contains("sold-out"));
    if (firstAvailableColor) this.setActiveColor(firstAvailableColor);
  }

  /**
   * Sort size options based on 'order' and hide items beyond the first 5.
   */
  sortAndFilterSizeOptions() {
    // Convert NodeList to an array for sorting
    const sortedSizes = Array.from(this.sizeOptions).sort((a, b) => {
      return parseInt(a.style.order, 10) - parseInt(b.style.order, 10); // Sort by 'order' value
    });

    // Display the first 5 items and hide the rest
    sortedSizes.forEach((item, index) => {
      item.style.display = index < 5 ? "inline-block" : "none";
    });
  }

  /**
   * Update the product image based on the selected color option.
   */
  updateCardMedia(colorOption) {
    if (!this.thumbnail) return; // Exit if no thumbnail is available

    // Get image-related attributes from the selected color option
    const imageSrc = colorOption.getAttribute("data-image-src");
    const srcset = colorOption.getAttribute("data-srcset");
    const imageAlt = colorOption.getAttribute("data-image-alt");

    // Update the thumbnail image attributes
    if (imageSrc) this.thumbnail.setAttribute("src", imageSrc);
    if (srcset) this.thumbnail.setAttribute("srcset", srcset);
    this.thumbnail.setAttribute("sizes", "500px");
    if (imageAlt) this.thumbnail.setAttribute("alt", imageAlt);
  }

  /**
   * Set the active color option and update related components.
   */
  setActiveColor(colorOption) {
    // Remove the 'active' class from all color options
    this.colorOptions.forEach((option) => option.classList.remove("active"));

    // Add 'active' class to the selected color option
    colorOption.classList.add("active");
    this.currentActiveColor = colorOption;

    // Update product image and overview text
    this.updateCardMedia(colorOption);
    this.updateOverview(colorOption);

    // Re-check and update size availability for the selected color
    this.updateSizes(colorOption.getAttribute("data-sizes"), colorOption);
  }

  /**
   * Update size availability based on the provided data and manage sold-out state.
   */
  updateSizes(sizesData, colorOption) {
    if (!sizesData || !this.sizeContainer) return; // Exit if no size data or container is available

    // Parse sizes data into a map of size -> availability
    const sizeMap = sizesData.split(",").reduce((map, sizePair) => {
      const [size, available] = sizePair.split(":").map((v) => v.trim());
      map[size] = available === "true"; // Convert availability to boolean
      return map;
    }, {});

    // Check if all sizes are sold out
    const allSoldOut = Object.values(sizeMap).every((available) => !available);

    // Find or create the sold-out message element
    let soldOutMessage = this.sizeContainer.querySelector(".sold-out-message");
    if (!soldOutMessage) {
      soldOutMessage = document.createElement("span");
      soldOutMessage.className = "sold-out-message hidden";
      soldOutMessage.textContent = "Sold out";
      this.sizeContainer.appendChild(soldOutMessage);
    }

    if (allSoldOut) {
      // Show the sold-out message and hide all size options
      soldOutMessage.classList.remove("hidden");
      this.sizeOptions.forEach((sizeOption) => {
        sizeOption.style.display = "none"; // Hide all size options
      });
      this.sizeContainer.classList.add("sold-out");

      // Mark the current color option as sold-out
      if (colorOption) colorOption.classList.add("sold-out");
    } else {
      // Hide the sold-out message and update size options
      soldOutMessage.classList.add("hidden");
      this.sizeContainer.classList.remove("sold-out");

      // Update each size option based on availability
      const sizeOptions = Array.from(this.sizeOptions);
      sizeOptions.forEach((sizeOption) => {
        const size = sizeOption.getAttribute("data-size");
        const isAvailable = sizeMap[size] || false;

        // Update size option classes and visibility
        sizeOption.classList.toggle("highlight", isAvailable);
        sizeOption.classList.toggle("disabled", !isAvailable);
        sizeOption.setAttribute("data-available", isAvailable.toString());
      });

      // Re-sort and re-filter size options
      this.sortAndFilterSizeOptions();

      // Ensure the current color option is not marked as sold-out
      if (colorOption) colorOption.classList.remove("sold-out");
    }
  }

  /**
   * Update the overview text to display the selected color.
   */
  updateOverview(colorOption) {
    const colorName = colorOption.getAttribute("data-color");
    if (this.overviewSelected) {
      this.overviewSelected.textContent = colorName; // Update the selected color text
    }
  }
}

// Define the custom element
customElements.define("card-options", CardOptions);

class TypeWriterElement extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.txtElement = document.createElement("span");
    this.txtElement.classList.add("txt");
    this.shadowRoot.appendChild(this.txtElement);

    this.words = this.getAttribute("words").split(",");
    this.wait = parseInt(this.getAttribute("wait"), 10) || 3000;
    this.txt = "";
    this.wordIndex = 0;
    this.isDeleting = false;

    this.type();
  }

  type() {
    const current = this.wordIndex % this.words.length;
    const fullTxt = this.words[current];

    if (this.isDeleting) {
      this.txt = fullTxt.substring(0, this.txt.length - 1);
    } else {
      this.txt = fullTxt.substring(0, this.txt.length + 1);
    }

    this.txtElement.innerHTML = `${this.txt}`;

    let typeSpeed = 100;
    if (this.isDeleting) {
      typeSpeed /= 2;
    }

    if (!this.isDeleting && this.txt === fullTxt) {
      typeSpeed = this.wait;
      this.isDeleting = true;
    } else if (this.isDeleting && this.txt === "") {
      this.isDeleting = false;
      this.wordIndex++;
      typeSpeed = 300;
    }

    setTimeout(() => this.type(), typeSpeed);
  }
}

customElements.define("type-writer", TypeWriterElement);
